import { STORY_EVIDENCE_CAP, STORY_EXPERIENCE_SCENE_CAP } from '@/core/contracts/brain-team-story-experience';

export type BrainTeamApprovedStoryKind =
  | 'observation_journey'
  | 'comparison'
  | 'visual_diary'
  | 'travel_diary';

/** Bounded summary safe to cross the cognition boundary. It contains no
 * draft, review state, approval snapshot, or media URL. */
export interface BrainTeamApprovedStoryCatalogItem {
  story_id: string;
  revision: number;
  title: string;
  summary: string;
  kind: BrainTeamApprovedStoryKind;
  aliases: string[];
  tags: string[];
  scene_count: number;
  experience_schema_version?: 1 | 2;
  experience_context?: {
    schema_version?: 1 | 2;
    scenes: Array<{ id: string; target_id: string }>;
    layers: Array<{ id: string; target_ids: string[] }>;
  };
}

export interface BrainTeamStorySelection {
  schema_version: 1;
  story_id: string;
  revision: number;
  source: 'approved_story_library';
  reason: 'explicit_match' | 'single_available';
}

export interface BrainTeamStorySelectionRelease {
  schema_version: 1;
  status: 'released';
  source: 'approved_story_library';
  reason: 'new_intent';
}

const KINDS = new Set<BrainTeamApprovedStoryKind>([
  'observation_journey',
  'comparison',
  'visual_diary',
  'travel_diary',
]);
const STORY_ID = /^[a-f0-9]{24}$/;
const exactKeys = (raw: Record<string, unknown>, expected: readonly string[]): boolean => {
  const actual = Object.keys(raw).sort();
  const wanted = [...expected].sort();
  return actual.length === wanted.length && actual.every((key, index) => key === wanted[index]);
};

const bounded = (value: unknown, max: number): string =>
  typeof value === 'string' && value.trim().length > 0 && value.trim().length <= max ? value.trim() : '';

const boundedList = (value: unknown, maxItems: number, maxLength: number): string[] =>
  Array.isArray(value)
    ? Array.from(new Set(value.map((item) => bounded(item, maxLength)).filter(Boolean))).slice(0, maxItems)
    : [];

/** Runtime whitelist. TypeScript cannot stop a caller from attaching an
 * approved projection or draft metadata to an otherwise valid summary. */
export function toApprovedStoryCatalogItem(value: unknown): BrainTeamApprovedStoryCatalogItem | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const storyId = bounded(raw.story_id, 192);
  const revision = raw.revision;
  const title = bounded(raw.title, 160);
  const summary = bounded(raw.summary, 480);
  const kind = raw.kind as BrainTeamApprovedStoryKind;
  const sceneCount = raw.scene_count;
  const experienceVersion = raw.experience_schema_version;
  const contextVersion =
    raw.experience_context && typeof raw.experience_context === 'object'
      ? (raw.experience_context as Record<string, unknown>).schema_version
      : undefined;
  // Up to 96 rows, told in time or not. A collection experience is always a comparison, so one on
  // any other kind stays at 24: the ask service refuses it, and one refused item fails the whole ask.
  const sceneLimit =
    (experienceVersion === 2 || contextVersion === 2) && kind !== 'comparison'
      ? STORY_EXPERIENCE_SCENE_CAP
      : STORY_EVIDENCE_CAP;
  if (
    (experienceVersion !== undefined && experienceVersion !== 1 && experienceVersion !== 2) ||
    !storyId ||
    !STORY_ID.test(storyId) ||
    !title ||
    !KINDS.has(kind) ||
    typeof revision !== 'number' ||
    !Number.isSafeInteger(revision) ||
    revision < 1 ||
    typeof sceneCount !== 'number' ||
    !Number.isSafeInteger(sceneCount) ||
    sceneCount < 2 ||
    sceneCount > sceneLimit
  ) {
    return null;
  }
  const experienceContext =
    raw.experience_context === undefined
      ? undefined
      : parseApprovedStoryExperienceContext(raw.experience_context);
  if (raw.experience_context !== undefined && !experienceContext) return null;
  if (experienceVersion && experienceContext && experienceVersion !== (experienceContext.schema_version ?? 1))
    return null;
  return {
    story_id: storyId,
    revision,
    title,
    summary,
    kind,
    aliases: boundedList(raw.aliases, 8, 160),
    tags: boundedList(raw.tags, 8, 80),
    scene_count: sceneCount,
    ...(experienceVersion === 1 || experienceVersion === 2
      ? { experience_schema_version: experienceVersion }
      : {}),
    ...(experienceContext ? { experience_context: experienceContext } : {}),
  };
}

export function parseApprovedStoryExperienceContext(
  value: unknown,
): BrainTeamApprovedStoryCatalogItem['experience_context'] | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const version = raw.schema_version === undefined ? 1 : raw.schema_version;
  if (
    (version !== 1 && version !== 2) ||
    !exactKeys(
      raw,
      raw.schema_version === undefined ? ['scenes', 'layers'] : ['schema_version', 'scenes', 'layers'],
    ) ||
    !Array.isArray(raw.scenes) ||
    !raw.scenes.length ||
    // A story told in time names up to 96 stops; a collection's authored scenes stay at most 24.
    raw.scenes.length > (version === 2 ? 24 : 96) ||
    !Array.isArray(raw.layers) ||
    raw.layers.length > 8
  )
    return null;
  const validId = (id: unknown): id is string =>
    typeof id === 'string' && id.length > 0 && id.length <= 192 && id.trim() === id;
  const scenes: Array<{ id: string; target_id: string }> = [];
  const layers: Array<{ id: string; target_ids: string[] }> = [];
  for (const item of raw.scenes) {
    if (
      !item ||
      typeof item !== 'object' ||
      !exactKeys(item, ['id', 'target_id']) ||
      !validId(item.id) ||
      !validId(item.target_id) ||
      scenes.some((scene) => scene.id === item.id)
    )
      return null;
    scenes.push({ id: item.id, target_id: item.target_id });
  }
  for (const item of raw.layers) {
    if (
      !item ||
      typeof item !== 'object' ||
      !exactKeys(item, ['id', 'target_ids']) ||
      !validId(item.id) ||
      layers.some((layer) => layer.id === item.id) ||
      !Array.isArray(item.target_ids) ||
      item.target_ids.length > (version === 2 ? 96 : 24) ||
      !item.target_ids.every(validId) ||
      new Set(item.target_ids).size !== item.target_ids.length
    )
      return null;
    layers.push({ id: item.id, target_ids: [...item.target_ids] });
  }
  if (version === 2 && new Set(layers.flatMap((layer) => layer.target_ids)).size > 96) return null;
  return { ...(raw.schema_version !== undefined ? { schema_version: version } : {}), scenes, layers };
}

export function parseBrainTeamStorySelection(value: unknown): BrainTeamStorySelection | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  const storyId = bounded(raw.story_id, 192);
  const revision = raw.revision;
  if (
    !exactKeys(raw, ['schema_version', 'story_id', 'revision', 'source', 'reason']) ||
    raw.schema_version !== 1 ||
    !storyId ||
    !STORY_ID.test(storyId) ||
    typeof revision !== 'number' ||
    !Number.isSafeInteger(revision) ||
    revision < 1 ||
    raw.source !== 'approved_story_library' ||
    (raw.reason !== 'explicit_match' && raw.reason !== 'single_available')
  ) {
    return undefined;
  }
  return {
    schema_version: 1,
    story_id: storyId,
    revision,
    source: 'approved_story_library',
    reason: raw.reason,
  };
}

/** A non-null selection value owns the turn even when strict parsing rejects
 * it. This tri-state keeps malformed or explicit release directives from
 * reviving an older approved story after a live turn or history refresh. */
export function hasBrainTeamStorySelectionDirective(value: unknown): boolean {
  return value !== null && value !== undefined;
}

export function parseBrainTeamStorySelectionRelease(
  value: unknown,
): BrainTeamStorySelectionRelease | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  if (
    !exactKeys(raw, ['schema_version', 'status', 'source', 'reason']) ||
    raw.schema_version !== 1 ||
    raw.status !== 'released' ||
    raw.source !== 'approved_story_library' ||
    raw.reason !== 'new_intent'
  ) {
    return undefined;
  }
  return {
    schema_version: 1,
    status: 'released',
    source: 'approved_story_library',
    reason: 'new_intent',
  };
}

export type BrainTeamStorySelectionDirective =
  | { kind: 'absent' }
  | { kind: 'selection'; selection: BrainTeamStorySelection }
  | { kind: 'release'; release: BrainTeamStorySelectionRelease }
  | { kind: 'invalid' };

/** One strict classifier shared by live SSE and persisted history. Invalid
 * non-null input remains authoritative so neither surface can revive an older
 * story through a permissive fallback. */
export function classifyBrainTeamStorySelectionDirective(value: unknown): BrainTeamStorySelectionDirective {
  if (!hasBrainTeamStorySelectionDirective(value)) return { kind: 'absent' };
  const selection = parseBrainTeamStorySelection(value);
  if (selection) return { kind: 'selection', selection };
  const release = parseBrainTeamStorySelectionRelease(value);
  if (release) return { kind: 'release', release };
  return { kind: 'invalid' };
}
